import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { formatIST } from '../lib/formatDate';
import { useAuth } from '../context/AuthContext';
import { ALL_MODULES } from '../lib/modules';
import { 
  Users, 
  Link as LinkIcon, 
  Layout, 
  Lock, 
  Mail, 
  Settings, 
  Download, 
  Activity, 
  Plus, 
  Trash2, 
  Check, 
  AlertTriangle, 
  RotateCw, 
  Eye, 
  Key, 
  Upload, 
  Copy, 
  Save, 
  CheckCircle2, 
  Clock, 
  SlidersHorizontal,
  X,
  UserCheck,
  FileText,
  DollarSign
} from 'lucide-react';

interface AdminPortalProps {
  onClose: () => void;
}

interface DirectoryUser {
  id: string;
  email: string;
  full_name: string;
  role: 'Super Admin' | 'Admin' | 'Member';
  modules: string[];
  status: 'active' | 'deactivated';
  created_at: string;
  last_login: string | null;
}

interface AuditLog {
  id: string;
  user: string;
  action: string;
  details: string;
  timestamp: string;
  type: 'security' | 'user' | 'integration' | 'export' | 'branding';
}

export default function AdminPortal({ onClose }: AdminPortalProps) {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'users' | 'integrations' | 'dashboards' | 'security' | 'email' | 'branding' | 'export' | 'audit'>('users');
  
  const adminMainRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    if (adminMainRef.current) {
      adminMainRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  // --- STATE 1: USERS (real, backed by /api/admin/users) ---
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);

  const fetchUsers = () => {
    setUsersLoading(true);
    api.get('/admin/users')
      .then((res) => setUsers(res.data.users))
      .catch(() => {})
      .finally(() => setUsersLoading(false));
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFullName, setInviteFullName] = useState('');
  const [inviteRole, setInviteRole] = useState<'Super Admin' | 'Admin' | 'Member'>('Member');
  const [inviteModules, setInviteModules] = useState<string[]>([]);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ email: string; emailed: boolean; temp_password: string | null } | null>(null);
  const [inviteError, setInviteError] = useState('');
  
  // --- STATE 3: INTEGRATIONS ---
  const [unicommerceCreds, setUnicommerceCreds] = useState({
    tenantId: '',
    username: '',
    password: '',
    facilityCodes: ''
  });
  const [unicommerceConfigured, setUnicommerceConfigured] = useState(false);
  const [unicommerceSaving, setUnicommerceSaving] = useState(false);
  const [unicommerceSyncing, setUnicommerceSyncing] = useState(false);
  const [unicommerceSyncInfo, setUnicommerceSyncInfo] = useState<any>(null);
  const [googleSheetsJson, setGoogleSheetsJson] = useState('{\n  "type": "service_account",\n  "project_id": "nooe-intelligence-studio",\n  "private_key_id": "8f2a4e9b1c7d0e3f",\n  "client_email": "sheets-sync@nooe-intelligence-studio.iam.gserviceaccount.com"\n}');
  const [sheetsSyncTarget, setSheetsSyncTarget] = useState('Design Tracker Master Google Sheet');
  const [sheetsStatus, setSheetsStatus] = useState<any>(null);
  const [sheetsSyncing, setSheetsSyncing] = useState(false);
  const [zohoStatus, setZohoStatus] = useState<any>(null);
  const [zohoSyncing, setZohoSyncing] = useState(false);
  const [shopifyStatus, setShopifyStatus] = useState<any>(null);
  const [shopifySyncing, setShopifySyncing] = useState(false);

  const loadSheetsStatus = () => {
    api.get('/integrations/google-sheets/status').then((res) => setSheetsStatus(res.data)).catch(() => {});
  };

  const loadZohoStatus = () => {
    api.get('/integrations/zoho/status').then((res) => setZohoStatus(res.data)).catch(() => {});
  };

  const loadShopifyStatus = () => {
    api.get('/integrations/shopify/status').then((res) => setShopifyStatus(res.data)).catch(() => {});
  };

  useEffect(() => {
    loadZohoStatus();
    loadShopifyStatus();
  }, []);

  const handleConnectShopify = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/oauth/shopify/login`;
  };

  const handleSyncShopifyNow = async () => {
    setShopifySyncing(true);
    try {
      await api.post('/integrations/shopify/sync');
      // fire-and-forget on the backend — a full sync takes minutes, so poll status shortly after
      setTimeout(loadShopifyStatus, 8000);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Shopify sync failed.');
    } finally {
      setShopifySyncing(false);
    }
  };

  const handleConnectZoho = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/oauth/zoho/login`;
  };

  const handleSyncZohoNow = async () => {
    setZohoSyncing(true);
    try {
      await api.post('/integrations/zoho/sync');
      setTimeout(loadZohoStatus, 8000);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Zoho Books sync failed.');
    } finally {
      setZohoSyncing(false);
    }
  };

  const loadUnicommerceConfig = () => {
    api.get('/integrations/unicommerce/config').then((res) => {
      const d = res.data;
      setUnicommerceConfigured(d.configured);
      setUnicommerceCreds((prev) => ({
        ...prev,
        tenantId: d.tenant_id || '',
        username: d.username || '',
        facilityCodes: (d.facility_codes || []).join(', '),
        password: '',
      }));
    }).catch(() => {});
  };

  useEffect(() => {
    loadUnicommerceConfig();
  }, []);

  const handleUnicommerceSyncNow = async () => {
    setUnicommerceSyncing(true);
    try {
      await api.post('/integrations/unicommerce/sync');
      // fire-and-forget on the backend — takes minutes, shows "started" until next status check
      setUnicommerceSyncInfo({ status: 'started' });
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Unicommerce sync failed.');
    } finally {
      setUnicommerceSyncing(false);
    }
  };

  useEffect(() => {
    loadSheetsStatus();
  }, []);

  const handleConnectGoogleSheets = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/oauth/sheets/login`;
  };

  const handleSyncSheetsNow = async () => {
    setSheetsSyncing(true);
    try {
      await api.post('/integrations/google-sheets/sync');
      setTimeout(loadSheetsStatus, 8000);
    } catch (e) {
      // surfaced via status card staying "not connected"
    } finally {
      setSheetsSyncing(false);
    }
  };
  
  const [testingConnection, setTestingConnection] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ [key: string]: 'success' | 'failed' }>({});
  const [syncStatus, setSyncStatus] = useState<{ [key: string]: { status: 'connected' | 'disconnected', lastSync: string } }>({
    metaAds: { status: 'connected', lastSync: '10 mins ago' },
    googleAds: { status: 'connected', lastSync: '10 mins ago' },
    hubspot: { status: 'connected', lastSync: '2 hours ago' },
    shopify: { status: 'connected', lastSync: 'Just now' },
    bitespeed: { status: 'connected', lastSync: 'Just now' },
    unicommerce: { status: 'connected', lastSync: '30 mins ago' },
    googleSheets: { status: 'connected', lastSync: '5 mins ago' }
  });

  // --- STATE 4: DASHBOARDS ---
  const [widgets, setWidgets] = useState([
    { id: '1', name: 'Sales Performance Multi-chart', enabled: true, type: 'line', size: 'large' },
    { id: '2', name: 'Paid Social ROAS Counter', enabled: true, type: 'donut', size: 'small' },
    { id: '3', name: 'Corporate Pipeline Funnel', enabled: true, type: 'bar', size: 'medium' },
    { id: '4', name: 'SKU Velocity Speedometer', enabled: true, type: 'table', size: 'medium' },
    { id: '5', name: 'Logistics SLA Pipeline Tracker', enabled: true, type: 'bar', size: 'large' }
  ]);
  const [roleLandingPage, setRoleLandingPage] = useState({
    'Revenue Ops': 'meta-ads',
    'B2B': 'hubspot',
    'Design': 'design-tracker',
    'Warehouse': 'unicommerce',
    'Admin': 'overview'
  });

  // --- STATE 5: SECURITY ---
  const [activeSessions, setActiveSessions] = useState([
    { id: 's1', device: 'Chrome on macOS (San Francisco, CA)', ip: '192.168.1.44', current: true },
    { id: 's2', device: 'Safari on iPhone 15 Pro (New York, NY)', ip: '172.56.21.90', current: false },
    { id: 's3', device: 'Firefox on Windows (Mumbai, IN)', ip: '103.45.198.12', current: false }
  ]);

  // --- STATE 6: EMAIL & ALERTS ---
  const [resendApiKey, setResendApiKey] = useState('re_9Jz2LKxs_Pf8HnS3D2bLm6Ka1Zp9Qe4');
  const [invitationBranding, setInvitationBranding] = useState('NOOE — Intelligence Studio (HQ)');
  const [alerts, setAlerts] = useState({
    adSpendCrosses: '500000',
    roasDropsBelow: '2.5',
    inventoryUnitsBelow: '150',
    supportTicketsAbove: '40',
    zohoOverdueDays: '15'
  });

  // --- STATE 7: BRANDING ---
  const [companyName, setCompanyName] = useState('NOOE — Intelligence Studio');
  const [accentColor, setAccentColor] = useState('#f97316'); // Orange
  const [selectedCurrency, setSelectedCurrency] = useState('₹');
  const [timezone, setTimezone] = useState('Asia/Kolkata (IST)');
  const [isDarkMode, setIsDarkMode] = useState(false);

  // --- STATE 8: DATA EXPORT ---
  const [scheduleReports, setScheduleReports] = useState([
    { id: 'r1', targetRole: 'Revenue Ops', format: 'PDF', frequency: 'Daily (08:00 AM)' },
    { id: 'r2', targetRole: 'Finance', format: 'CSV', frequency: 'Weekly (Monday morning)' }
  ]);
  const [newScheduleRole, setNewScheduleRole] = useState('Revenue Ops');
  const [newScheduleFormat, setNewScheduleFormat] = useState('PDF');
  const [newScheduleFreq, setNewScheduleFreq] = useState('Daily (08:00 AM)');

  // --- STATE 9: AUDIT LOGS ---
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([
    { id: 'a1', user: 'piyush@nooe.co', action: 'User Invitation', details: 'Sent invite link to cx-agent@nooe.co', timestamp: '2026-07-15 14:22:11', type: 'user' },
    { id: 'a2', user: 'piyush@nooe.co', action: 'Integration Edit', details: 'Updated Make.com webhook URL for Shopify DTC', timestamp: '2026-07-14 09:15:30', type: 'integration' },
    { id: 'a3', user: 'bhavya@nooe.co', action: 'Data Export', details: 'Downloaded CSV export for Meta Ads ROI report', timestamp: '2026-07-13 18:04:55', type: 'export' },
    { id: 'a4', user: 'piyush@nooe.co', action: 'Security Update', details: 'Forced session revocation for old Firefox client', timestamp: '2026-07-12 11:45:00', type: 'security' },
    { id: 'a5', user: 'piyush@nooe.co', action: 'Branding Configuration', details: 'Changed primary accent color code to #f97316', timestamp: '2026-07-11 16:30:12', type: 'branding' }
  ]);
  const [logFilter, setLogFilter] = useState<string>('all');

  // --- ACTION HANDLERS ---
  const logAction = (action: string, details: string, type: AuditLog['type']) => {
    const newLog: AuditLog = {
      id: 'a' + (auditLogs.length + 1),
      user: currentUser?.email || 'system',
      action,
      details,
      timestamp: new Date().toISOString().replace('T', ' ').substring(0, 19),
      type
    };
    setAuditLogs([newLog, ...auditLogs]);
  };

  const toggleInviteModule = (moduleId: string) => {
    setInviteModules((prev) => prev.includes(moduleId) ? prev.filter((m) => m !== moduleId) : [...prev, moduleId]);
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setInviteError('');
    setInviteSubmitting(true);
    try {
      const res = await api.post('/admin/users/invite', {
        email: inviteEmail,
        full_name: inviteFullName,
        role: inviteRole,
        modules: inviteRole === 'Member' ? inviteModules : [],
      });
      setInviteResult({ email: inviteEmail, emailed: res.data.emailed, temp_password: res.data.temp_password });
      logAction('User Invitation', `Invited ${inviteEmail} as ${inviteRole}${res.data.emailed ? ' (emailed via Resend)' : ' (Resend not connected — temp password shown to Super Admin)'}`, 'user');
      setInviteEmail('');
      setInviteFullName('');
      setInviteModules([]);
      fetchUsers();
    } catch (e: any) {
      setInviteError(e?.response?.data?.detail || 'Failed to send invitation.');
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleResetPassword = async (userId: string, email: string) => {
    try {
      const res = await api.post(`/admin/users/${userId}/reset-password`);
      if (res.data.emailed) {
        alert(`A new password has been emailed to ${email}.`);
      } else {
        alert(`Resend isn't connected yet — share this temporary password with ${email} directly:\n\n${res.data.temp_password}`);
      }
      logAction('Password Reset', `Generated new credentials for ${email}`, 'security');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to reset password.');
    }
  };

  const handleDeleteUser = async (userId: string, email: string) => {
    if (!confirm(`Are you sure you want to permanently remove ${email}?`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers(users.filter((u) => u.id !== userId));
      logAction('User Deleted', `Removed user ${email}`, 'user');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to remove user.');
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: DirectoryUser['status']) => {
    const nextStatus = currentStatus === 'active' ? 'deactivated' : 'active';
    try {
      await api.patch(`/admin/users/${userId}/status`, { status: nextStatus });
      setUsers(users.map((u) => (u.id === userId ? { ...u, status: nextStatus } : u)));
      logAction('User Status Toggle', `Toggled status of ${users.find((u) => u.id === userId)?.email} to ${nextStatus}`, 'user');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to update status.');
    }
  };

  const testConnection = (id: string) => {
    setTestingConnection(id);
    setTimeout(() => {
      setTestingConnection(null);
      setTestResult(prev => ({ ...prev, [id]: 'success' }));
      setSyncStatus(prev => ({
        ...prev,
        [id]: { ...prev[id], status: 'connected', lastSync: 'Just now' }
      }));
      logAction('Integration Sync Tested', `Successfully validated connection pipeline credentials for: ${id}`, 'integration');
    }, 1500);
  };

  const handleSaveIntegrations = async () => {
    setUnicommerceSaving(true);
    try {
      await api.post('/integrations/unicommerce/config', {
        tenant_id: unicommerceCreds.tenantId,
        username: unicommerceCreds.username,
        password: unicommerceCreds.password || undefined,
        facility_codes: unicommerceCreds.facilityCodes.split(',').map((c) => c.trim()).filter(Boolean),
      });
      loadUnicommerceConfig();
      logAction('Webhooks Configurations Updated', 'Saved new webhook endpoints and Unicommerce credentials', 'integration');
      alert('Unicommerce credentials saved successfully.');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to save configuration. Please try again.');
    } finally {
      setUnicommerceSaving(false);
    }
  };

  const moveWidget = (index: number, direction: 'up' | 'down') => {
    const newWidgets = [...widgets];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= widgets.length) return;
    
    const temp = newWidgets[index];
    newWidgets[index] = newWidgets[targetIndex];
    newWidgets[targetIndex] = temp;
    setWidgets(newWidgets);
    logAction('Dashboard Customization', 'Rearranged layout widget priorities on the front dashboard', 'branding');
  };

  const handleWidgetToggle = (id: string) => {
    setWidgets(widgets.map(w => w.id === id ? { ...w, enabled: !w.enabled } : w));
  };

  const handleSaveDashboardCustomization = () => {
    logAction('Dashboard Structure Saved', 'Saved custom landing layouts, chart selectors, and widget configs', 'branding');
    alert('Dashboard settings synchronized. Users will view their role-specific landing panels now.');
  };

  const handleSaveBranding = () => {
    logAction('Company Branding Updated', `Configured tenant logo configurations and accent: ${accentColor}`, 'branding');
    alert('Branding criteria saved. Application headers updated to match new styling guides.');
  };

  const handleSaveEmailAlerts = () => {
    logAction('Email Thresholds Configuration', 'Saved automated triggers for inventory, financial, and spend notifications', 'branding');
    alert('Alert criteria established. Automated Resend emails will dispatch on criteria matches.');
  };

  const handleAddSchedule = (e: React.FormEvent) => {
    e.preventDefault();
    const newReport = {
      id: 'r' + (scheduleReports.length + 1),
      targetRole: newScheduleRole,
      format: newScheduleFormat,
      frequency: newScheduleFreq
    };
    setScheduleReports([...scheduleReports, newReport]);
    logAction('Report Schedule Added', `Created ${newScheduleFormat} email delivery script for ${newScheduleRole}`, 'export');
  };

  const handleRemoveSchedule = (id: string) => {
    setScheduleReports(scheduleReports.filter(r => r.id !== id));
    logAction('Report Schedule Deleted', 'Removed recurring automated summary report schedule', 'export');
  };

  const mockExportAction = (format: 'PDF' | 'CSV') => {
    alert(`Compiling complete database schema...\nGenerating static ${format} dashboard artifact...\n\nYour download will commence in a moment.`);
    logAction('Database Exported', `Admin executed a full manual schema backup to: ${format}`, 'export');
  };

  const filteredLogs = auditLogs.filter(log => {
    if (logFilter === 'all') return true;
    return log.type === logFilter;
  });

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 select-none animate-fadeIn">
      <div className="bg-white w-full max-w-6xl h-[88vh] rounded-[2rem] shadow-2xl border border-slate-200 overflow-hidden flex flex-col">
        
        {/* Header segment matching Bento design */}
        <div className="px-8 py-5 bg-slate-50 border-b border-slate-200/80 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center font-bold text-white shadow-md text-base">
              N
            </div>
            <div>
              <h1 className="text-base font-bold tracking-tight text-slate-800">NOOE — Administrative Facilities Control</h1>
              <p className="text-[10px] font-mono uppercase tracking-widest text-orange-500 font-bold">Comprehensive Tenancy Management Console</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-200/60 rounded-full transition-all duration-300 text-slate-500 hover:text-slate-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Inner layout with structural sidebar & active window panel */}
        <div className="flex-1 flex overflow-hidden">
          
          {/* Side Menu Tab Bar */}
          <aside className="w-64 bg-slate-50 border-r border-slate-200/60 flex flex-col justify-between shrink-0 overflow-y-auto p-4">
            <div className="space-y-1">
              <span className="px-3 text-[9px] font-mono uppercase tracking-widest text-slate-400 font-bold block mb-3">FACILITY CATGORIES</span>
              
              <button
                onClick={() => setActiveTab('users')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-left ${
                  activeTab === 'users' ? 'bg-white text-slate-800 border border-slate-200 shadow-sm font-semibold' : 'text-slate-600 hover:bg-slate-200/40 hover:text-slate-800'
                }`}
              >
                <Users className="h-4 w-4 text-orange-500 shrink-0" />
                <span className="text-xs">9.1 User Management</span>
              </button>

              <button
                onClick={() => setActiveTab('integrations')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-left ${
                  activeTab === 'integrations' ? 'bg-white text-slate-800 border border-slate-200 shadow-sm font-semibold' : 'text-slate-600 hover:bg-slate-200/40 hover:text-slate-800'
                }`}
              >
                <LinkIcon className="h-4 w-4 text-sky-500 shrink-0" />
                <span className="text-xs">9.3 Integrations Page</span>
              </button>

              <button
                onClick={() => setActiveTab('dashboards')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-left ${
                  activeTab === 'dashboards' ? 'bg-white text-slate-800 border border-slate-200 shadow-sm font-semibold' : 'text-slate-600 hover:bg-slate-200/40 hover:text-slate-800'
                }`}
              >
                <Layout className="h-4 w-4 text-violet-500 shrink-0" />
                <span className="text-xs">9.4 Dashboard Custom</span>
              </button>

              <button
                onClick={() => setActiveTab('security')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-left ${
                  activeTab === 'security' ? 'bg-white text-slate-800 border border-slate-200 shadow-sm font-semibold' : 'text-slate-600 hover:bg-slate-200/40 hover:text-slate-800'
                }`}
              >
                <Lock className="h-4 w-4 text-emerald-500 shrink-0" />
                <span className="text-xs">9.5 Security Core</span>
              </button>

              <button
                onClick={() => setActiveTab('email')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-left ${
                  activeTab === 'email' ? 'bg-white text-slate-800 border border-slate-200 shadow-sm font-semibold' : 'text-slate-600 hover:bg-slate-200/40 hover:text-slate-800'
                }`}
              >
                <Mail className="h-4 w-4 text-amber-500 shrink-0" />
                <span className="text-xs">9.6 Email & Alerts</span>
              </button>

              <button
                onClick={() => setActiveTab('branding')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-left ${
                  activeTab === 'branding' ? 'bg-white text-slate-800 border border-slate-200 shadow-sm font-semibold' : 'text-slate-600 hover:bg-slate-200/40 hover:text-slate-800'
                }`}
              >
                <Settings className="h-4 w-4 text-rose-500 shrink-0" />
                <span className="text-xs">9.7 Company Branding</span>
              </button>

              <button
                onClick={() => setActiveTab('export')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-left ${
                  activeTab === 'export' ? 'bg-white text-slate-800 border border-slate-200 shadow-sm font-semibold' : 'text-slate-600 hover:bg-slate-200/40 hover:text-slate-800'
                }`}
              >
                <Download className="h-4 w-4 text-teal-500 shrink-0" />
                <span className="text-xs">9.8 Data Export / Schedule</span>
              </button>

              <button
                onClick={() => setActiveTab('audit')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-left ${
                  activeTab === 'audit' ? 'bg-white text-slate-800 border border-slate-200 shadow-sm font-semibold' : 'text-slate-600 hover:bg-slate-200/40 hover:text-slate-800'
                }`}
              >
                <Activity className="h-4 w-4 text-indigo-500 shrink-0" />
                <span className="text-xs">9.9 System Audit Log</span>
              </button>
            </div>

            <div className="p-3 bg-slate-100 rounded-2xl border border-slate-200">
              <span className="text-[9px] uppercase tracking-wider text-slate-400 font-bold block mb-1">TENANT USER STATUS</span>
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                <span className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse"></span>
                <span>ADMIN ROOT ACCESS</span>
              </div>
            </div>
          </aside>

          {/* Central Main Stage Tab Panel */}
          <main ref={adminMainRef} className="flex-1 bg-white p-8 overflow-y-auto h-full">
            
            {/* 9.1: USER MANAGEMENT PANEL */}
            {activeTab === 'users' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <Users className="h-5 w-5 text-orange-500" /> User Directory & Invites
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">Manage corporate personnel rosters and trigger secure single invitations.</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Invite via Email Component */}
                  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200/80 space-y-4" data-testid="invite-user-panel">
                    <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold">Send Single Invitation</h3>
                    <form onSubmit={handleInvite} className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500 mb-1">Email Address</label>
                          <input 
                            type="email" 
                            required
                            data-testid="invite-email-input"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            placeholder="collaborator@nooe.co"
                            className="w-full bg-white border border-slate-200 px-3.5 py-2 text-xs rounded-xl focus:outline-none focus:border-slate-400 font-medium"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500 mb-1">Full Name</label>
                          <input 
                            type="text" 
                            data-testid="invite-fullname-input"
                            value={inviteFullName}
                            onChange={(e) => setInviteFullName(e.target.value)}
                            placeholder="Optional"
                            className="w-full bg-white border border-slate-200 px-3.5 py-2 text-xs rounded-xl focus:outline-none focus:border-slate-400 font-medium"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500 mb-1">Assign Role</label>
                        <select 
                          data-testid="invite-role-select"
                          value={inviteRole}
                          onChange={(e) => setInviteRole(e.target.value as 'Super Admin' | 'Admin' | 'Member')}
                          className="w-full bg-white border border-slate-200 px-3 py-2 text-xs rounded-xl focus:outline-none font-medium text-slate-700"
                        >
                          <option value="Member">Member (choose specific modules)</option>
                          <option value="Admin">Admin (full dashboard access, no console)</option>
                          <option value="Super Admin">Super Admin (full access + console)</option>
                        </select>
                      </div>

                      {inviteRole === 'Member' && (
                        <div className="space-y-2">
                          <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500">Select Modules</label>
                          <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-y-auto p-2 border border-slate-200/80 bg-white rounded-xl" data-testid="invite-modules-checklist">
                            {ALL_MODULES.map((mod) => (
                              <label key={mod.id} className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer hover:bg-slate-50 p-1 rounded">
                                <input 
                                  type="checkbox"
                                  data-testid={`invite-module-checkbox-${mod.id}`}
                                  checked={inviteModules.includes(mod.id)}
                                  onChange={() => toggleInviteModule(mod.id)}
                                  className="rounded text-slate-800 focus:ring-slate-500 h-3.5 w-3.5"
                                />
                                <span>{mod.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      <button 
                        type="submit"
                        disabled={inviteSubmitting}
                        data-testid="dispatch-invite-button"
                        className="w-full bg-slate-800 hover:bg-slate-950 text-white font-semibold text-xs py-2 px-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-1.5 disabled:opacity-50"
                      >
                        <Mail className="h-3.5 w-3.5" /> {inviteSubmitting ? 'Sending…' : 'Dispatch Invite'}
                      </button>
                    </form>
                    {inviteError && (
                      <div data-testid="invite-error-message" className="p-3 bg-rose-50 border border-rose-100 rounded-xl text-rose-700 flex items-center gap-2 text-xs font-medium">
                        <AlertTriangle className="h-4 w-4" /> {inviteError}
                      </div>
                    )}
                    {inviteResult && (
                      <div data-testid="invite-success-message" className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl text-emerald-800 space-y-1 text-xs font-medium">
                        <div className="flex items-center gap-2"><Check className="h-4 w-4" /> Invitation created for {inviteResult.email}</div>
                        {inviteResult.emailed ? (
                          <p className="text-[11px] font-normal">Credentials were emailed via Resend.</p>
                        ) : (
                          <p className="text-[11px] font-normal">Resend isn't connected yet — share this temporary password directly: <code className="font-mono font-bold">{inviteResult.temp_password}</code></p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Roster summary card */}
                  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200/80 flex flex-col justify-center">
                    <span className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold mb-2">Roster Snapshot</span>
                    <div className="grid grid-cols-3 gap-3 mt-2">
                      <div className="bg-white rounded-xl p-3 text-center border border-slate-200/60">
                        <p className="text-xl font-bold text-slate-800">{users.filter((u) => u.role === 'Super Admin').length}</p>
                        <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold mt-0.5">Super Admin</p>
                      </div>
                      <div className="bg-white rounded-xl p-3 text-center border border-slate-200/60">
                        <p className="text-xl font-bold text-slate-800">{users.filter((u) => u.role === 'Admin').length}</p>
                        <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold mt-0.5">Admin</p>
                      </div>
                      <div className="bg-white rounded-xl p-3 text-center border border-slate-200/60">
                        <p className="text-xl font-bold text-slate-800">{users.filter((u) => u.role === 'Member').length}</p>
                        <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold mt-0.5">Member</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Users list grid */}
                <div className="border border-slate-200/80 rounded-2xl overflow-hidden shadow-xs bg-white">
                  <div className="bg-slate-50/60 p-4 border-b border-slate-100 flex items-center justify-between">
                    <span className="text-xs font-mono uppercase tracking-widest text-slate-400 font-bold">System Roster List ({users.length} users)</span>
                    <span className="text-[10px] text-slate-400 font-mono">{usersLoading ? 'Loading…' : 'Last Synchronized: Real-time active'}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse" data-testid="users-table">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-100 text-[10px] uppercase font-mono tracking-wider font-bold text-slate-400">
                          <th className="p-4">Collaborator Address</th>
                          <th className="p-4">Assigned Role</th>
                          <th className="p-4">Modules</th>
                          <th className="p-4">State</th>
                          <th className="p-4">Last Login</th>
                          <th className="p-4 text-right">Administrative Options</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-xs font-medium">
                        {users.map((u) => (
                          <tr key={u.id} className="hover:bg-slate-50/40 transition-all duration-200" data-testid={`user-row-${u.email}`}>
                            <td className="p-4 font-semibold text-slate-800">{u.email}</td>
                            <td className="p-4">
                              <span className="px-2 py-0.5 bg-slate-100 border border-slate-200/60 text-slate-600 rounded text-[10px] font-semibold">
                                {u.role}
                              </span>
                            </td>
                            <td className="p-4 text-slate-500 text-[10px] max-w-[220px]">
                              {u.role === 'Member' ? u.modules.join(', ') : 'All modules'}
                            </td>
                            <td className="p-4">
                              <button 
                                onClick={() => handleToggleUserStatus(u.id, u.status)}
                                data-testid={`toggle-status-${u.email}`}
                                title={u.id === currentUser?.id ? 'You cannot change your own status' : 'Toggle active/deactivated'}
                                disabled={u.id === currentUser?.id}
                                className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border capitalize disabled:opacity-40 disabled:cursor-not-allowed ${
                                  u.status === 'active'
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                                    : 'bg-rose-50 text-rose-700 border-rose-100'
                                }`}
                              >
                                {u.status}
                              </button>
                            </td>
                            <td className="p-4 text-slate-500 font-mono text-[11px]">{u.last_login ? formatIST(u.last_login) : 'Never'}</td>
                            <td className="p-4 text-right space-x-1">
                              <button 
                                onClick={() => handleResetPassword(u.id, u.email)}
                                data-testid={`reset-password-${u.email}`}
                                title="Reset User Password"
                                className="p-1.5 hover:bg-slate-100 hover:text-slate-800 rounded transition-all text-slate-400"
                              >
                                <Key className="h-3.5 w-3.5" />
                              </button>
                              <button 
                                onClick={() => handleDeleteUser(u.id, u.email)}
                                data-testid={`delete-user-${u.email}`}
                                title="Remove User Profile"
                                disabled={u.id === currentUser?.id}
                                className="p-1.5 hover:bg-rose-50 hover:text-rose-600 rounded transition-all text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* 9.3: INTEGRATIONS PAGE PANEL */}
            {activeTab === 'integrations' && (
              <div className="space-y-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <LinkIcon className="h-5 w-5 text-sky-500" /> Integrations & Real-time Live Webhooks
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">Configure endpoint connection webhooks, service files, API credentials, and sync schedules.</p>
                  </div>
                  <button 
                    onClick={handleSaveIntegrations}
                    disabled={unicommerceSaving}
                    data-testid="save-integrations-config-button"
                    className="bg-slate-800 hover:bg-slate-950 text-white font-semibold text-xs py-2 px-4 rounded-xl transition-all duration-300 flex items-center gap-1.5 shadow-sm disabled:opacity-50"
                  >
                    <Save className="h-3.5 w-3.5" /> {unicommerceSaving ? 'Saving…' : 'Save Configuration'}
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Bitespeed webhook panel */}
                  <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-4 shadow-xs">
                      <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold border-b border-slate-100 pb-2 flex justify-between items-center">
                        <span>Bitespeed (CSAT Webhook)</span>
                        <span className="text-[9px] bg-emerald-50 text-emerald-700 px-2.5 py-0.5 rounded border border-emerald-100">Live endpoint</span>
                      </h3>
                      
                      <div className="space-y-2">
                        <p className="text-[11px] text-slate-500">
                          Deploy this static generated URL into Bitespeed Developers webhook setting page to automatically feed incoming WhatsApp sentiment metrics.
                        </p>
                        <div className="flex gap-2">
                          <input 
                            type="text" 
                            readOnly
                            value={`${process.env.REACT_APP_BACKEND_URL}/api/webhooks/bitespeed`}
                            className="flex-1 bg-slate-50 border border-slate-200 px-3 py-2 text-[11.5px] font-mono rounded-xl focus:outline-none text-slate-500"
                          />
                          <button 
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/webhooks/bitespeed`);
                              alert('Bitespeed endpoint webhooks copied to local clipboard.');
                            }}
                            className="bg-slate-100 hover:bg-slate-200 border border-slate-200 hover:border-slate-300 p-2 rounded-xl text-slate-600 transition-all"
                            title="Copy webhook"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Unicommerce Tenant Panel */}
                    <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-4 shadow-xs">
                      <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold border-b border-slate-100 pb-2 flex justify-between items-center">
                        <span>Unicommerce Logistics Credentials</span>
                        <span
                          data-testid="unicommerce-configured-badge"
                          className={`text-[9px] font-mono font-bold px-2.5 py-0.5 rounded-full border uppercase ${
                            unicommerceConfigured ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-slate-50 text-slate-400 border-slate-200'
                          }`}
                        >
                          {unicommerceConfigured ? 'Configured' : 'Not Configured'}
                        </span>
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500 mb-1">Tenant ID</label>
                          <input 
                            type="text" 
                            data-testid="unicommerce-tenant-input"
                            value={unicommerceCreds.tenantId}
                            onChange={(e) => setUnicommerceCreds({ ...unicommerceCreds, tenantId: e.target.value })}
                            placeholder="e.g. palindromedesignindiaprivatelimited"
                            className="w-full bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs rounded-xl focus:outline-none text-slate-700 font-medium"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500 mb-1">Username</label>
                          <input 
                            type="text" 
                            data-testid="unicommerce-username-input"
                            value={unicommerceCreds.username}
                            onChange={(e) => setUnicommerceCreds({ ...unicommerceCreds, username: e.target.value })}
                            className="w-full bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs rounded-xl focus:outline-none text-slate-700 font-medium"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500 mb-1">Password</label>
                          <input 
                            type="password" 
                            data-testid="unicommerce-password-input"
                            value={unicommerceCreds.password}
                            onChange={(e) => setUnicommerceCreds({ ...unicommerceCreds, password: e.target.value })}
                            placeholder={unicommerceConfigured ? '•••••••• (unchanged)' : ''}
                            className="w-full bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs rounded-xl focus:outline-none text-slate-700 font-medium"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500 mb-1">Facility Code(s)</label>
                          <input 
                            type="text" 
                            data-testid="unicommerce-facility-codes-input"
                            value={unicommerceCreds.facilityCodes}
                            onChange={(e) => setUnicommerceCreds({ ...unicommerceCreds, facilityCodes: e.target.value })}
                            placeholder="e.g. FAC001, FAC002"
                            className="w-full bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs rounded-xl focus:outline-none text-slate-700 font-medium"
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-1">
                        <span data-testid="unicommerce-sync-status" className="text-[10px] text-slate-400 font-mono">
                          {unicommerceSyncInfo?.status === 'started' ? 'Sync started — refreshing in the background, check back shortly' : 'No sync yet'}
                        </span>
                        <button
                          data-testid="unicommerce-sync-now-button"
                          onClick={handleUnicommerceSyncNow}
                          disabled={!unicommerceConfigured || unicommerceSyncing}
                          className="px-4 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {unicommerceSyncing ? 'Syncing…' : 'Sync Now'}
                        </button>
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-4 shadow-xs">
                      <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                        <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold">
                          Google Sheets — OAuth Connection
                        </h3>
                        <span
                          data-testid="google-sheets-connection-badge"
                          className={`text-[9px] font-mono font-bold px-2.5 py-0.5 rounded-full border uppercase ${
                            sheetsStatus?.connected
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                              : 'bg-slate-50 text-slate-400 border-slate-200'
                          }`}
                        >
                          {sheetsStatus?.connected ? 'Connected' : 'Not Connected'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-[11px]">
                        <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                          <p className="text-[9px] uppercase font-mono font-bold text-slate-400 mb-1">Design Tracker</p>
                          <p className="font-semibold text-slate-700">{sheetsStatus?.design_tracker_tabs ?? 0} tabs synced</p>
                          <p className="text-slate-400 font-mono text-[10px] mt-0.5">
                            {sheetsStatus?.design_tracker_last_sync ? formatIST(sheetsStatus.design_tracker_last_sync) : 'never'}
                          </p>
                        </div>
                        <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                          <p className="text-[9px] uppercase font-mono font-bold text-slate-400 mb-1">Category Ops</p>
                          <p className="font-semibold text-slate-700">{sheetsStatus?.category_ops_tabs ?? 0} tabs synced</p>
                          <p className="text-slate-400 font-mono text-[10px] mt-0.5">
                            {sheetsStatus?.category_ops_last_sync ? formatIST(sheetsStatus.category_ops_last_sync) : 'never'}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          data-testid="google-sheets-connect-button"
                          onClick={handleConnectGoogleSheets}
                          className="flex-1 bg-slate-900 text-white text-xs font-semibold px-4 py-2.5 rounded-xl hover:bg-slate-800 transition-colors"
                        >
                          {sheetsStatus?.connected ? 'Reconnect Google Account' : 'Connect with Google'}
                        </button>
                        <button
                          data-testid="google-sheets-sync-now-button"
                          onClick={handleSyncSheetsNow}
                          disabled={!sheetsStatus?.connected || sheetsSyncing}
                          className="px-4 py-2.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {sheetsSyncing ? 'Syncing…' : 'Sync Now'}
                        </button>
                      </div>
                      <p className="text-[10px] text-slate-400">
                        Reads two sheets read-only every 30 minutes: Design and Development Tracker → Design module, Category Ops → Merchandising module.
                      </p>
                    </div>

                    {/* Zoho Books OAuth Panel */}
                    <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-4 shadow-xs">
                      <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                        <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold">
                          Zoho Books — OAuth Connection (India DC)
                        </h3>
                        <span
                          data-testid="zoho-connection-badge"
                          className={`text-[9px] font-mono font-bold px-2.5 py-0.5 rounded-full border uppercase ${
                            zohoStatus?.connected
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                              : 'bg-slate-50 text-slate-400 border-slate-200'
                          }`}
                        >
                          {zohoStatus?.connected ? 'Connected' : 'Not Connected'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-[11px]">
                        <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                          <p className="text-[9px] uppercase font-mono font-bold text-slate-400 mb-1">Invoices Synced</p>
                          <p className="font-semibold text-slate-700">{zohoStatus?.invoices_count ?? 0} records</p>
                          <p className="text-slate-400 font-mono text-[10px] mt-0.5">
                            {zohoStatus?.synced_at ? formatIST(zohoStatus.synced_at) : 'never'}
                          </p>
                        </div>
                        <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                          <p className="text-[9px] uppercase font-mono font-bold text-slate-400 mb-1">Organization ID</p>
                          <p className="font-semibold text-slate-700">801063961</p>
                          <p className="text-slate-400 font-mono text-[10px] mt-0.5">accounts.zoho.in</p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          data-testid="zoho-connect-button"
                          onClick={handleConnectZoho}
                          className="flex-1 bg-slate-900 text-white text-xs font-semibold px-4 py-2.5 rounded-xl hover:bg-slate-800 transition-colors"
                        >
                          {zohoStatus?.connected ? 'Reconnect Zoho Books' : 'Connect Zoho Books'}
                        </button>
                        <button
                          data-testid="zoho-sync-now-button"
                          onClick={handleSyncZohoNow}
                          disabled={!zohoStatus?.connected || zohoSyncing}
                          className="px-4 py-2.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {zohoSyncing ? 'Syncing…' : 'Sync Now'}
                        </button>
                      </div>
                      <p className="text-[10px] text-slate-400">
                        Fetches the latest Invoices, Estimates, Expenses and Customer Payments (7-day window) into the Finance module every 30 minutes.
                      </p>
                    </div>

                    {/* Shopify OAuth Panel */}
                    <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-4 shadow-xs">
                      <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                        <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold">
                          Shopify — OAuth Connection (nooe-india.myshopify.com)
                        </h3>
                        <span
                          data-testid="shopify-connection-badge"
                          className={`text-[9px] font-mono font-bold px-2.5 py-0.5 rounded-full border uppercase ${
                            shopifyStatus?.connected
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                              : 'bg-slate-50 text-slate-400 border-slate-200'
                          }`}
                        >
                          {shopifyStatus?.connected ? 'Connected' : 'Not Connected'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-[11px]">
                        <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                          <p className="text-[9px] uppercase font-mono font-bold text-slate-400 mb-1">Orders Synced</p>
                          <p className="font-semibold text-slate-700">{shopifyStatus?.orders_count ?? 0} records</p>
                          <p className="text-slate-400 font-mono text-[10px] mt-0.5">
                            {shopifyStatus?.orders_synced_at ? formatIST(shopifyStatus.orders_synced_at) : 'never'}
                          </p>
                        </div>
                        <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                          <p className="text-[9px] uppercase font-mono font-bold text-slate-400 mb-1">Products Synced</p>
                          <p className="font-semibold text-slate-700">{shopifyStatus?.products_count ?? 0} records</p>
                          <p className="text-slate-400 font-mono text-[10px] mt-0.5">
                            {shopifyStatus?.products_synced_at ? formatIST(shopifyStatus.products_synced_at) : 'never'}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          data-testid="shopify-connect-button"
                          onClick={handleConnectShopify}
                          className="flex-1 bg-slate-900 text-white text-xs font-semibold px-4 py-2.5 rounded-xl hover:bg-slate-800 transition-colors"
                        >
                          {shopifyStatus?.connected ? 'Reconnect Shopify' : 'Connect Shopify'}
                        </button>
                        <button
                          data-testid="shopify-sync-now-button"
                          onClick={handleSyncShopifyNow}
                          disabled={!shopifyStatus?.connected || shopifySyncing}
                          className="px-4 py-2.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {shopifySyncing ? 'Syncing…' : 'Sync Now'}
                        </button>
                      </div>
                      <p className="text-[10px] text-slate-400">
                        Fetches Orders (30-day window) into the Shopify DTC module, and the full Products + Inventory catalog into the Merchandising and Tags modules, every 30 minutes.
                      </p>
                    </div>

                  </div>

                {/* Status directory listing with Connection checks */}
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                  <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold mb-4">Connection Active Channels directory</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.entries(syncStatus).map(([key, item]) => {
                      const syncItem = item as { status: 'connected' | 'disconnected'; lastSync: string };
                      return (
                        <div key={key} className="bg-white border border-slate-200/80 rounded-xl p-4 flex flex-col justify-between h-28 shadow-2xs">
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="text-xs font-bold text-slate-800 uppercase tracking-wider capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                              <span className="block text-[9px] text-slate-400 font-mono mt-0.5">Synced {syncItem.lastSync}</span>
                            </div>
                            <span className={`h-2 w-2 rounded-full ${
                              syncItem.status === 'connected' ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'
                            }`}></span>
                          </div>

                          <div className="flex justify-between items-center mt-3">
                            <span className="text-[10px] text-slate-500 font-semibold font-mono uppercase">
                              {syncItem.status === 'connected' ? 'Active Sync' : 'Offline'}
                            </span>
                            
                            <button 
                              type="button"
                              onClick={() => testConnection(key)}
                              disabled={testingConnection === key}
                              className="text-[10px] bg-slate-100 hover:bg-slate-200 border border-slate-200 hover:border-slate-300 font-semibold text-slate-700 py-1 px-2.5 rounded-lg flex items-center gap-1 transition-all"
                            >
                              {testingConnection === key ? (
                                <>
                                  <RotateCw className="h-3 w-3 animate-spin text-orange-500" /> Checking...
                                </>
                              ) : testResult[key] === 'success' ? (
                                <>
                                  <Check className="h-3 w-3 text-emerald-500" /> Connected
                                </>
                              ) : (
                                'Test Stream'
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* 9.4: DASHBOARD CUSTOMIZATION PANEL */}
            {activeTab === 'dashboards' && (
              <div className="space-y-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <Layout className="h-5 w-5 text-violet-500" /> Dashboard Widgets & Landing Pages
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">Configure layout cards arrangement, default chart displays, custom widgets, and role landing views.</p>
                  </div>
                  <button 
                    onClick={handleSaveDashboardCustomization}
                    className="bg-slate-800 hover:bg-slate-950 text-white font-semibold text-xs py-2 px-4 rounded-xl transition-all duration-300 flex items-center gap-1.5 shadow-sm"
                  >
                    <Save className="h-3.5 w-3.5" /> Save Configuration
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Widget list & arrangement */}
                  <div className="lg:col-span-2 space-y-4">
                    <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold mb-2">Drag & Arrange Dashboard Widgets</h3>
                    <p className="text-[11px] text-slate-500 leading-relaxed mb-4">
                      Reorder the chronological display layout of widgets below using priority sliders. Check/uncheck boxes to activate or suppress visualization panels globally.
                    </p>

                    <div className="space-y-3">
                      {widgets.map((w, index) => (
                        <div key={w.id} className="bg-white border border-slate-200 p-4 rounded-2xl flex items-center justify-between shadow-3xs hover:border-slate-300/80 transition-all">
                          <div className="flex items-center gap-3">
                            <input 
                              type="checkbox"
                              checked={w.enabled}
                              onChange={() => handleWidgetToggle(w.id)}
                              className="rounded text-slate-800 focus:ring-slate-500 h-4 w-4"
                            />
                            <div>
                              <span className="text-xs font-bold text-slate-800">{w.name}</span>
                              <div className="flex gap-2 items-center text-[10px] text-slate-400 font-mono uppercase mt-0.5">
                                <span>Default Type: {w.type}</span>
                                <span>•</span>
                                <span>Scale: {w.size}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {/* Selector for chart rendering preference */}
                            <select 
                              value={w.type}
                              onChange={(e) => {
                                const nw = [...widgets];
                                nw[index].type = e.target.value;
                                setWidgets(nw);
                              }}
                              className="bg-slate-50 border border-slate-200/80 px-2 py-1 text-[10.5px] rounded-lg focus:outline-none font-semibold text-slate-600"
                            >
                              <option value="line">Line Chart</option>
                              <option value="bar">Bar Chart</option>
                              <option value="donut">Donut Chart</option>
                              <option value="table">Data Table</option>
                            </select>

                            <button 
                              onClick={() => moveWidget(index, 'up')}
                              disabled={index === 0}
                              className="p-1 hover:bg-slate-100 disabled:opacity-40 text-slate-500 rounded"
                              title="Move Up"
                            >
                              ▲
                            </button>
                            <button 
                              onClick={() => moveWidget(index, 'down')}
                              disabled={index === widgets.length - 1}
                              className="p-1 hover:bg-slate-100 disabled:opacity-40 text-slate-500 rounded"
                              title="Move Down"
                            >
                              ▼
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Landing view configs per role */}
                  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200/80 space-y-4 h-fit">
                    <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold">Role Default Landing Targets</h3>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      Configure custom default loading destinations when users log in. They will automatically start on their designated segment focus.
                    </p>

                    <div className="space-y-3.5 pt-2">
                      {Object.keys(roleLandingPage).map((role) => (
                        <div key={role} className="space-y-1">
                          <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500">{role} Landing View</label>
                          <select 
                            value={roleLandingPage[role]}
                            onChange={(e) => setRoleLandingPage({ ...roleLandingPage, [role]: e.target.value })}
                            className="w-full bg-white border border-slate-200 px-3 py-1.5 text-xs rounded-xl focus:outline-none font-semibold text-slate-700"
                          >
                            <option value="overview">Overview Unified Dashboard</option>
                            <option value="meta-ads">Meta Ads Social ROAS</option>
                            <option value="shopify">Shopify DTC Sales</option>
                            <option value="google-ads">Google Ads SEM</option>
                            <option value="hubspot">Hubspot Gifting Leads</option>
                            <option value="design-tracker">Design Tracker Master</option>
                            <option value="shopify-merch">Shopify Merchandising</option>
                            <option value="category-ops">Category Margins Matrix</option>
                            <option value="unicommerce">Unicommerce Logistics</option>
                            <option value="zoho">Zoho Invoice Books</option>
                            <option value="bitespeed">Bitespeed Sentiment</option>
                            <option value="shopify-tags">Shopify VIP segments</option>
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 9.5: SECURITY Core PANEL */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <Lock className="h-5 w-5 text-emerald-500" /> Security Policies & Revocation Logs
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">Trace corporate login metadata and inspect active user sessions.</p>
                </div>

                <div className="max-w-2xl space-y-4">
                  <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold">Active User Sessions</h3>
                  <p className="text-[11px] text-slate-500">
                    Real-time session authorization logs. Administrators can force instant remote session termination on individual users.
                  </p>

                  <div className="space-y-3">
                    {activeSessions.map((session) => (
                      <div key={session.id} className="bg-white border border-slate-200 p-4 rounded-2xl flex justify-between items-center shadow-3xs">
                        <div className="flex gap-3 items-center">
                          <SlidersHorizontal className="h-4.5 w-4.5 text-slate-400 shrink-0" />
                          <div>
                            <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                              {session.device}
                              {session.current && (
                                <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 text-[8.5px] rounded border border-emerald-100 uppercase tracking-wider font-bold">Active client</span>
                              )}
                            </span>
                            <span className="block text-[10px] text-slate-400 font-mono mt-0.5">Host Address: {session.ip}</span>
                          </div>
                        </div>

                        {!session.current && (
                          <button 
                            type="button"
                            onClick={() => {
                              setActiveSessions(activeSessions.filter(s => s.id !== session.id));
                              logAction('Session Revoked', `Revoked token session details for device: ${session.device}`, 'security');
                              alert(`Revoked login cookie validation for: ${session.device}`);
                            }}
                            className="text-[10px] bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold py-1 px-2 rounded-lg border border-rose-100 transition-all"
                          >
                            Terminate
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 9.6: EMAIL & NOTIFICATIONS PANEL */}
            {activeTab === 'email' && (
              <div className="space-y-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <Mail className="h-5 w-5 text-amber-500" /> Email Configurations & Automated Alerts
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">Configure Resend email credentials, custom branding headers, and automatic alerts for threshold crossings.</p>
                  </div>
                  <button 
                    onClick={handleSaveEmailAlerts}
                    className="bg-slate-800 hover:bg-slate-950 text-white font-semibold text-xs py-2 px-4 rounded-xl transition-all duration-300 flex items-center gap-1.5 shadow-sm"
                  >
                    <Save className="h-3.5 w-3.5" /> Save Configuration
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Resend setup panel */}
                  <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-4 shadow-xs">
                    <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold border-b border-slate-100 pb-2">
                      Resend API Credentials
                    </h3>

                    <div>
                      <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500 mb-1">Resend API Key Token</label>
                      <input 
                        type="password" 
                        value={resendApiKey}
                        onChange={(e) => setResendApiKey(e.target.value)}
                        placeholder="re_••••••••••••••••••••••••"
                        className="w-full bg-slate-50 border border-slate-200 px-3 py-2 text-xs font-mono rounded-xl focus:outline-none focus:border-slate-400 text-slate-700 font-medium"
                      />
                      <span className="text-[10px] text-slate-400 block font-semibold mt-1">Utilized for dispatching invitation links, daily data reports, and metric alarms.</span>
                    </div>

                    <div>
                      <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500 mb-1">Invitation Brand Heading Name</label>
                      <input 
                        type="text" 
                        value={invitationBranding}
                        onChange={(e) => setInvitationBranding(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 px-3 py-2 text-xs rounded-xl focus:outline-none focus:border-slate-400 font-semibold text-slate-700"
                      />
                    </div>
                  </div>

                  {/* Automated alert thresholds */}
                  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200/80 space-y-4">
                    <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold">Automated Notification Alarms (Email Alerts)</h3>
                    <p className="text-[11px] text-slate-500">
                      Configure telemetry limits. Resend dispatch scripts run every hour to analyze sync data. An alert dispatches instantly to authorized managers if conditions trigger.
                    </p>

                    <div className="space-y-3 pt-1">
                      <div className="grid grid-cols-2 gap-3 items-center">
                        <span className="text-xs font-medium text-slate-700">Ad Spend crosses ₹X</span>
                        <input 
                          type="text" 
                          value={alerts.adSpendCrosses}
                          onChange={(e) => setAlerts({ ...alerts, adSpendCrosses: e.target.value })}
                          className="bg-white border border-slate-200 px-3 py-1.5 text-xs font-mono rounded-xl focus:outline-none w-full font-semibold text-slate-700 text-right"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3 items-center">
                        <span className="text-xs font-medium text-slate-700">ROAS drops below Y</span>
                        <input 
                          type="text" 
                          value={alerts.roasDropsBelow}
                          onChange={(e) => setAlerts({ ...alerts, roasDropsBelow: e.target.value })}
                          className="bg-white border border-slate-200 px-3 py-1.5 text-xs font-mono rounded-xl focus:outline-none w-full font-semibold text-slate-700 text-right"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3 items-center">
                        <span className="text-xs font-medium text-slate-700">Inventory falls below Z units</span>
                        <input 
                          type="text" 
                          value={alerts.inventoryUnitsBelow}
                          onChange={(e) => setAlerts({ ...alerts, inventoryUnitsBelow: e.target.value })}
                          className="bg-white border border-slate-200 px-3 py-1.5 text-xs font-mono rounded-xl focus:outline-none w-full font-semibold text-slate-700 text-right"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3 items-center">
                        <span className="text-xs font-medium text-slate-700">WhatsApp tickets cross N</span>
                        <input 
                          type="text" 
                          value={alerts.supportTicketsAbove}
                          onChange={(e) => setAlerts({ ...alerts, supportTicketsAbove: e.target.value })}
                          className="bg-white border border-slate-200 px-3 py-1.5 text-xs font-mono rounded-xl focus:outline-none w-full font-semibold text-slate-700 text-right"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3 items-center">
                        <span className="text-xs font-medium text-slate-700">Zoho invoice overdue by D days</span>
                        <input 
                          type="text" 
                          value={alerts.zohoOverdueDays}
                          onChange={(e) => setAlerts({ ...alerts, zohoOverdueDays: e.target.value })}
                          className="bg-white border border-slate-200 px-3 py-1.5 text-xs font-mono rounded-xl focus:outline-none w-full font-semibold text-slate-700 text-right"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 9.7: COMPANY / BRANDING PANEL */}
            {activeTab === 'branding' && (
              <div className="space-y-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <Settings className="h-5 w-5 text-rose-500" /> Company Identity & Branding presets
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">Configure system title criteria, choose primary theme accent, customize currency configurations and timezones.</p>
                  </div>
                  <button 
                    onClick={handleSaveBranding}
                    className="bg-slate-800 hover:bg-slate-950 text-white font-semibold text-xs py-2 px-4 rounded-xl transition-all duration-300 flex items-center gap-1.5 shadow-sm"
                  >
                    <Save className="h-3.5 w-3.5" /> Save Configuration
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Branding form configurations */}
                  <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-4 shadow-xs">
                    <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold border-b border-slate-100 pb-2">
                      Identity Configuration
                    </h3>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500 mb-1">Company Workspace Name</label>
                        <input 
                          type="text" 
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          className="w-full bg-slate-50 border border-slate-200 px-3.5 py-2 text-xs rounded-xl focus:outline-none focus:border-slate-400 font-semibold text-slate-700"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500 mb-1">Theme Contrast Color</label>
                          <div className="flex gap-2">
                            <input 
                              type="color" 
                              value={accentColor}
                              onChange={(e) => setAccentColor(e.target.value)}
                              className="w-8 h-8 rounded cursor-pointer border border-slate-200/80"
                            />
                            <input 
                              type="text" 
                              value={accentColor}
                              onChange={(e) => setAccentColor(e.target.value)}
                              className="flex-1 bg-slate-50 border border-slate-200 px-2.5 py-1 text-xs font-mono rounded-xl focus:outline-none text-slate-700"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500 mb-1">System contrast Theme</label>
                          <button 
                            type="button"
                            onClick={() => setIsDarkMode(!isDarkMode)}
                            className="w-full bg-slate-50 border border-slate-200 hover:bg-slate-100 px-3.5 py-1.5 text-xs rounded-xl focus:outline-none font-semibold text-slate-700 text-center transition-all"
                          >
                            {isDarkMode ? 'Dark Contrast' : 'Light Contrast (Standard)'}
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500 mb-1">Exchange Currency Symbol</label>
                          <select 
                            value={selectedCurrency}
                            onChange={(e) => setSelectedCurrency(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs rounded-xl focus:outline-none font-semibold text-slate-700"
                          >
                            <option value="₹">₹ (Indian Rupee - INR)</option>
                            <option value="$">$ (United States Dollar - USD)</option>
                            <option value="€">€ (Euro - EUR)</option>
                            <option value="£">£ (British Pound - GBP)</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500 mb-1">Administrative Timezone</label>
                          <select 
                            value={timezone}
                            onChange={(e) => setTimezone(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs rounded-xl focus:outline-none font-semibold text-slate-700 text-ellipsis"
                          >
                            <option value="Asia/Kolkata (IST)">Asia/Kolkata (IST)</option>
                            <option value="America/New_York (EST)">America/New_York (EST)</option>
                            <option value="Europe/London (GMT)">Europe/London (GMT)</option>
                            <option value="Asia/Singapore (SGT)">Asia/Singapore (SGT)</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Logo branding preview info */}
                  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200/80 flex flex-col justify-between">
                    <div>
                      <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold mb-2">Corporate Asset Upload</h3>
                      <p className="text-[11px] text-slate-500 leading-relaxed">
                        Customize system branding. Upload a custom `.png` or vector company logotype to overwrite existing branding indicators. NOOE template formatting remains locked to clean bento grids.
                      </p>
                    </div>

                    <div className="border-2 border-dashed border-slate-200 bg-white/50 hover:bg-white rounded-2xl p-6 text-center cursor-pointer hover:border-slate-300 transition-all duration-300 mt-4">
                      <Upload className="h-6 w-6 text-slate-400 mx-auto mb-2" />
                      <span className="text-xs font-semibold block text-slate-700 mb-0.5">Drag & Drop Company Logotype</span>
                      <span className="text-[10px] text-slate-400 block font-mono">PNG, SVG formats (max width 250px)</span>
                    </div>

                    <div className="bg-white/80 border border-slate-200/60 p-4 rounded-xl mt-4 flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center font-bold text-white text-sm shrink-0">N</div>
                      <div>
                        <span className="text-xs font-bold text-slate-800">NOOE Asset Locked</span>
                        <p className="text-[10px] text-slate-400">Current branding is set to defaults.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 9.8: DATA EXPORT PANEL */}
            {activeTab === 'export' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <Download className="h-5 w-5 text-teal-500" /> Database Backup & Scheduled Reporting
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">Execute manual complete database exports or compile scheduled automated email alerts to specific roles.</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left instant backup buttons */}
                  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200/80 space-y-4 h-fit">
                    <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold">Manual On-Demand Export</h3>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      Download complete real-time synced tables and telemetry archives into clean static structured schemas instantly.
                    </p>

                    <div className="space-y-2 pt-2">
                      <button 
                        type="button"
                        onClick={() => mockExportAction('CSV')}
                        className="w-full bg-white hover:bg-slate-100 text-slate-700 font-semibold text-xs py-2.5 px-4 rounded-xl transition-all border border-slate-200 flex items-center justify-center gap-2 shadow-2xs"
                      >
                        <FileText className="h-4 w-4 text-emerald-500" /> Download Consolidated CSV
                      </button>

                      <button 
                        type="button"
                        onClick={() => mockExportAction('PDF')}
                        className="w-full bg-white hover:bg-slate-100 text-slate-700 font-semibold text-xs py-2.5 px-4 rounded-xl transition-all border border-slate-200 flex items-center justify-center gap-2 shadow-2xs"
                      >
                        <Download className="h-4 w-4 text-rose-500" /> Download Executive PDF Report
                      </button>
                    </div>
                  </div>

                  {/* Right scheduled emails configuration */}
                  <div className="lg:col-span-2 space-y-5">
                    <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-4 shadow-xs">
                      <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold border-b border-slate-100 pb-2">
                        Configure Scheduled Automated Email Reports
                      </h3>

                      <form onSubmit={handleAddSchedule} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                        <div>
                          <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500 mb-1">Target Team Role</label>
                          <select 
                            value={newScheduleRole}
                            onChange={(e) => setNewScheduleRole(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs rounded-xl focus:outline-none font-semibold text-slate-700"
                          >
                            <option>Revenue Ops</option>
                            <option>Finance</option>
                            <option>Warehouse</option>
                            <option>Design</option>
                            <option>CX</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500 mb-1">Export Format</label>
                          <select 
                            value={newScheduleFormat}
                            onChange={(e) => setNewScheduleFormat(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs rounded-xl focus:outline-none font-semibold text-slate-700"
                          >
                            <option value="PDF">PDF Document</option>
                            <option value="CSV">CSV Spreadsheet</option>
                          </select>
                        </div>

                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500 mb-1">Sync Frequency</label>
                            <select 
                              value={newScheduleFreq}
                              onChange={(e) => setNewScheduleFreq(e.target.value)}
                              className="w-full bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs rounded-xl focus:outline-none font-semibold text-slate-700"
                            >
                              <option>Daily (08:00 AM)</option>
                              <option>Weekly (Monday morning)</option>
                              <option>Monthly (1st at midnight)</option>
                            </select>
                          </div>
                          <button 
                            type="submit"
                            className="bg-slate-800 hover:bg-slate-950 p-2 text-white rounded-xl transition-all"
                            title="Register schedule"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </form>

                      {/* Schedule listings */}
                      <div className="space-y-2 pt-2">
                        <span className="text-[10px] font-mono uppercase text-slate-400 block font-bold">Active Automated Schedules</span>
                        {scheduleReports.map((r) => (
                          <div key={r.id} className="bg-slate-50 border border-slate-200/80 p-3.5 rounded-xl flex justify-between items-center text-xs">
                            <div className="flex items-center gap-3">
                              <Clock className="h-4 w-4 text-slate-400 shrink-0" />
                              <div>
                                <span className="font-bold text-slate-800">{r.targetRole} Team Sync</span>
                                <span className="block text-[10.5px] text-slate-400 font-mono">Dispatches {r.format} • {r.frequency} via Resend API</span>
                              </div>
                            </div>

                            <button 
                              type="button"
                              onClick={() => handleRemoveSchedule(r.id)}
                              className="p-1 hover:bg-rose-50 text-slate-400 hover:text-rose-600 rounded transition-all"
                              title="Delete schedule"
                            >
                              <Trash2 className="h-4.5 w-4.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 9.9: AUDIT LOG PANEL */}
            {activeTab === 'audit' && (
              <div className="space-y-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <Activity className="h-5 w-5 text-indigo-500" /> Administrative System Audit Trails
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">Chronological record directory trace of administrative tasks, logins, integration updates, and exports.</p>
                  </div>

                  <button 
                    type="button"
                    onClick={() => {
                      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(auditLogs, null, 2));
                      const downloadAnchor = document.createElement('a');
                      downloadAnchor.setAttribute("href",     dataStr);
                      downloadAnchor.setAttribute("download", `nooe_admin_audit_logs_${new Date().toISOString().split('T')[0]}.json`);
                      document.body.appendChild(downloadAnchor);
                      downloadAnchor.click();
                      downloadAnchor.remove();
                      logAction('Export System Logs', 'Admin exported consolidated system audit trails log as JSON file.', 'export');
                    }}
                    className="bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 font-semibold text-xs py-2 px-4 rounded-xl transition-all duration-300 flex items-center gap-1.5 shadow-2xs"
                  >
                    <Download className="h-3.5 w-3.5" /> Export Logs to JSON
                  </button>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-2 bg-slate-50 p-2.5 rounded-2xl border border-slate-200/60 text-xs">
                  <span className="font-mono text-[9px] uppercase tracking-widest text-slate-400 font-bold px-2">Filter Category:</span>
                  {['all', 'security', 'user', 'integration', 'export', 'branding'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setLogFilter(filter)}
                      className={`px-3 py-1 rounded-xl font-semibold transition-all capitalize text-[11px] ${
                        logFilter === filter ? 'bg-white shadow-3xs text-slate-800' : 'text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>

                {/* Audit trail details list */}
                <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white">
                  <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                    {filteredLogs.length > 0 ? (
                      filteredLogs.map((log) => (
                        <div key={log.id} className="p-4 hover:bg-slate-50/30 transition-all flex justify-between items-start text-xs gap-4">
                          <div className="flex gap-3 items-start">
                            <span className={`px-2 py-0.5 rounded text-[8.5px] font-mono uppercase font-bold border mt-0.5 ${
                              log.type === 'security' 
                                ? 'bg-emerald-50 border-emerald-100 text-emerald-700' 
                                : log.type === 'user'
                                ? 'bg-orange-50 border-orange-100 text-orange-700'
                                : log.type === 'integration'
                                ? 'bg-sky-50 border-sky-100 text-sky-700'
                                : log.type === 'export'
                                ? 'bg-teal-50 border-teal-100 text-teal-700'
                                : 'bg-rose-50 border-rose-100 text-rose-700'
                            }`}>
                              {log.type}
                            </span>
                            <div>
                              <span className="font-bold text-slate-800 text-[12.5px]">{log.action}</span>
                              <p className="text-slate-500 text-[11px] mt-0.5">{log.details}</p>
                              <span className="block text-[10px] text-slate-400 font-mono mt-1 font-semibold">User account context: {log.user}</span>
                            </div>
                          </div>

                          <span className="text-[10px] text-slate-400 font-mono shrink-0 font-semibold">{log.timestamp}</span>
                        </div>
                      ))
                    ) : (
                      <div className="p-8 text-center text-slate-400 text-xs">
                        No logs synchronized for this filter category.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
